import React, { useState, useMemo } from 'react';
import GroupResultsComponent from '../ui/GroupResultsComponent';
import { SocialSharingComponent } from '../ui/SocialSharingComponent';
import { getPositionsForFanType, getCoachAreasForFanType } from '../../utils/unified-fan-requirements';
import { getGradeColor } from '../../utils/unified-grade-utils';
import { calculateOverallTeamAssessment } from '../../utils/overall-team-assessment';

// Safe date -> string
const safeDateTime = (value) => {
  try {
    if (!value) return 'recently';
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? 'recently' : d.toLocaleString();
  } catch {
    return 'recently';
  }
};

// Completion status (aligned with unified fan tiers)
const calculateCompletionStatus = (gameData) => {
  const positionGrades = gameData?.positionGrades || {};
  const coachGrades = gameData?.coachGrades || {};
  const fanType = gameData?.fanType || 'casual';

  const requiredPositions = getPositionsForFanType(fanType) || [];
  const requiredCoachAreas = getCoachAreasForFanType(fanType) || [];

  const completedPositions = requiredPositions.filter(
    (p) => (positionGrades[`${p}_grade`] || '').trim() !== ''
  ).length;

  const completedCoachAreas = requiredCoachAreas.filter(
    (a) => (coachGrades[a] || '').trim() !== ''
  ).length;

  const totalRequired = requiredPositions.length + requiredCoachAreas.length;
  const totalCompleted = completedPositions + completedCoachAreas;
  const completionPercentage =
    totalRequired > 0 ? Math.round((totalCompleted / totalRequired) * 100) : 0;

  return {
    completionPercentage,
    isSubmitted: gameData?.status === 'submitted',
    completedPositions,
    totalRequired: requiredPositions.length,
    requiredPositions,
    requiredCoachAreas,
  };
};

export const ResultsTab = ({
  gameData,
  predictions,         // optional external predictions (kept for API parity)
  playerPredictions,    // optional external player preds (kept for API parity)
}) => {
  const [expandedSection, setExpandedSection] = useState(
    gameData?.status === 'submitted' ? 'completeResults' : null
  );

  const teamAssessment = useMemo(() => {
    if (!gameData) return null;
    try {
      return calculateOverallTeamAssessment(gameData);
    } catch {
      return null;
    }
  }, [gameData]);

  if (!gameData) return <div>Loading game data...</div>;

  let completion;
  try {
    completion = calculateCompletionStatus(gameData);
  } catch (e) {
    console.error('❌ Error calculating completion:', e);
    return <div>Error calculating completion status</div>;
  }

  const positionGrades = gameData?.positionGrades || {};
  const coachGrades = gameData?.coachGrades || {};

  // Prefer in-doc predictions; fall back to props if provided
  const gamePredictions = gameData?.predictions || predictions || {};
  const currentPlayerPredictions =
    gamePredictions?.topPlayer || playerPredictions || {};
  const isAnalysisLocked =
    completion?.isSubmitted || gameData?.status === 'submitted';

  const getPositionGrade = (position) =>
    positionGrades[`${position}_grade`] || 'N/A';

  const getCoachGrade = (areaId) => coachGrades[areaId] || 'N/A';

  const calculateGameByGameRecord = () => {
    try {
      const results = gamePredictions?.gameResults || {};
      const values = Object.values(results);
      if (!values.length) return '0-0';

      let wins = 0;
      let losses = 0;
      for (const v of values) {
        const s = String(v).toLowerCase();
        if (s === 'win' || s === 'w') wins += 1;
        else if (s === 'loss' || s === 'l') losses += 1;
      }
      const total = wins + losses;
      return total > 0 ? `${wins}-${losses}` : '0-0';
    } catch (e) {
      console.error('Error calculating record:', e);
      return '0-0';
    }
  };

  const getSortedCoachRankings = () => {
    try {
      const rankings = gamePredictions?.conferenceCoachRankings || {};
      return Object.entries(rankings)
        .filter(([_, rank]) => rank && `${rank}`.trim() !== '')
        .map(([coach, rank]) => ({ coach, rank: parseInt(rank, 10) }))
        .filter((r) => Number.isFinite(r.rank))
        .sort((a, b) => a.rank - b.rank);
    } catch (e) {
      console.error('Error getting coach rankings:', e);
      return [];
    }
  };

  const toggleSection = (section) => {
    setExpandedSection((prev) => (prev === section ? null : section));

    if (expandedSection !== section && section === 'groupResults') {
      setTimeout(() => {
        const el = document.querySelector('[data-section-id="groupResults"]');
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.pageYOffset - 20;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }, 150);
    }
  };

  // Nudge users who haven't done much
  if (completion.completionPercentage < 10) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          textAlign: 'center',
          backgroundColor: '#f9fafb',
          border: '2px dashed #d1d5db',
          borderRadius: 12,
          padding: 32,
        }}
      >
        <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
        <h3 style={{ color: '#374151', marginBottom: 8 }}>Start Your Analysis</h3>
        <p style={{ color: '#6b7280', marginBottom: 16, maxWidth: 400 }}>
          Complete some team analysis sections to see your results here.
        </p>
        <div
          style={{
            fontSize: 14,
            color: '#f59e0b',
            backgroundColor: '#fef3c7',
            padding: '8px 16px',
            borderRadius: 6,
          }}
        >
          Progress: {completion.completionPercentage}% Complete
        </div>
      </div>
    );
  }

  const hasOverallAssessment =
    !!teamAssessment &&
    ((teamAssessment?.positions?.completed || 0) > 0 ||
      (teamAssessment?.coaches?.completed || 0) > 0);

  return (
    <div className="results-tab" style={{ maxWidth: '1000px', margin: '0 auto' }}>
      {/* Fancy overall header if we have the assessment and analysis is locked */}
      {hasOverallAssessment && isAnalysisLocked && (
        <div
          style={{
            background: `linear-gradient(135deg, ${teamAssessment.overallColor}15 0%, ${teamAssessment.overallColor}25 100%)`,
            border: `3px solid ${teamAssessment.overallColor}`,
            borderRadius: 16,
            padding: '2.5rem 2rem',
            marginBottom: '2rem',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
              pointerEvents: 'none',
            }}
          />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🏆</div>
            <h1
              style={{
                fontSize: '2.5rem',
                fontWeight: 800,
                color: teamAssessment.overallColor,
                margin: '0 0 0.5rem 0',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              {gameData.team} {gameData.season} Analysis
            </h1>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2rem',
                flexWrap: 'wrap',
                marginBottom: '1.5rem',
              }}
            >
              <div
                style={{
                  background: 'rgba(255,255,255,0.9)',
                  borderRadius: 12,
                  padding: '1rem 2rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}
              >
                <div style={{ fontSize: '.9rem', color: '#6b7280', marginBottom: 4 }}>
                  Overall Team Grade
                </div>
                <div
                  style={{
                    fontSize: '3rem',
                    fontWeight: 900,
                    color: teamAssessment.overallColor,
                    lineHeight: 1,
                  }}
                >
                  {teamAssessment.overallGrade}
                </div>
                <div style={{ fontSize: '.8rem', color: '#6b7280' }}>
                  GPA: {teamAssessment.overallGPA}/4.0
                </div>
              </div>

              {teamAssessment?.predictions?.hasPredictions && (
                <div
                  style={{
                    background: 'rgba(255,255,255,0.9)',
                    borderRadius: 12,
                    padding: '1rem 2rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}
                >
                  <div style={{ fontSize: '.9rem', color: '#6b7280', marginBottom: 4 }}>
                    Predicted Record
                  </div>
                  <div
                    style={{
                      fontSize: '2.5rem',
                      fontWeight: 800,
                      color: '#374151',
                      lineHeight: 1,
                    }}
                  >
                    {teamAssessment.predictions.predictedRecord}
                  </div>
                  <div
                    style={{
                      fontSize: '.8rem',
                      color: teamAssessment.predictions.outlookColor,
                      fontWeight: 600,
                    }}
                  >
                    {teamAssessment.predictions.outlook}
                  </div>
                </div>
              )}
            </div>

            <div
              style={{
                background: 'rgba(255,255,255,0.8)',
                borderRadius: 8,
                padding: '1rem',
                fontSize: '1rem',
                color: '#374151',
                fontStyle: 'italic',
                maxWidth: 600,
                margin: '0 auto',
              }}
            >
              💡 {teamAssessment.summary.keyInsight}
            </div>
          </div>
        </div>
      )}

      {/* Simple header fallback */}
      {!hasOverallAssessment && (
        <div className="sbe-contrast-guard"
          style={{
            background:
              'linear-gradient(135deg, var(--sbe-primary-dark) 0%, var(--sbe-primary) 100%)',
            color: 'white',
            padding: '2rem',
            borderRadius: 12,
            marginBottom: '2rem',
            position: 'relative',
          }}
        >
          {isAnalysisLocked && (
            <div
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: 6,
                fontSize: '.9rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '.5rem',
              }}
            >
              🔒
            </div>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '1rem',
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: '1.9rem', fontWeight: 'bold' }}>🏆 Your Analysis Results</h2>
              <p style={{ margin: '.5rem 0 0 0', opacity: 0.98 }}>
                {isAnalysisLocked
                  ? `Complete analysis for ${gameData.team} ${gameData.season} season`
                  : `Analysis results for ${gameData.team} ${gameData.season} season`}
              </p>
            </div>
          </div>

          {isAnalysisLocked && gameData.submittedAt && (
            <p style={{ margin: '.5rem 0 0 0', opacity: 0.8, fontSize: '.9rem' }}>
              Submitted: {safeDateTime(gameData.submittedAt)}
            </p>
          )}

          <div
            style={{
              marginTop: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              fontSize: '.9rem',
            }}
          >
            <span>Status: {completion.completionPercentage}% Complete</span>
            <div
              style={{
                width: 100,
                height: 6,
                background: 'rgba(255,255,255,0.3)',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${completion.completionPercentage}%`,
                  height: '100%',
                  background: 'white',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Accordions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Team Assessment Breakdown (only when locked & available) */}
        {hasOverallAssessment && isAnalysisLocked && (
          <div
            style={{
              background: 'white',
              borderRadius: 12,
              border: `2px solid ${isAnalysisLocked ? '#10b981' : '#e5e7eb'}`,
              overflow: 'hidden',
              marginBottom: 24,
            }}
          >
            <button
              onClick={() => toggleSection('teamBreakdown')}
              style={{
                width: '100%',
                background: expandedSection === 'teamBreakdown' ? 'var(--sbe-primary)' : '#f8fafc',
                color: expandedSection === 'teamBreakdown' ? 'white' : '#374151',
                border: 'none',
                padding: '1.5rem',
                fontSize: '1.2rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.2s ease',
                scrollMarginTop: '120px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
                <span>📊</span>
                <div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 600, margin: 0 }}>
                    Team Assessment Breakdown
                  </div>
                  <div style={{ fontSize: '.9rem', opacity: 0.8, marginTop: '.25rem' }}>
                    Detailed analysis of positions, coaching, and season outlook
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                {isAnalysisLocked && <span style={{ fontSize: '.8rem' }}>🔒</span>}
                <span
                  style={{
                    transform: expandedSection === 'teamBreakdown' ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                >
                  ▼
                </span>
              </div>
            </button>

            {expandedSection === 'teamBreakdown' && (
              <div style={{ padding: '2rem', borderTop: '1px solid #e5e7eb' }}>
                {/* Position Overview */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4
                    style={{
                      margin: '0 0 .75rem 0',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: 'var(--sbe-primary)',
                    }}
                  >
                    🏈 Overall Position Group Analysis
                  </h4>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '.75rem',
                    }}
                  >
                    <div
                      style={{
                        padding: '.75rem',
                        background: '#f8fafc',
                        borderRadius: 6,
                        borderLeft: `4px solid ${getGradeColor(
                          convertGPAToLetterGrade(teamAssessment.positions.gpa)
                        )}`,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '.85rem', fontWeight: 500, color: '#374151' }}>
                          📊 Position Grade
                        </span>
                        <span
                          style={{
                            fontSize: '.9rem',
                            fontWeight: 'bold',
                            color: getGradeColor(convertGPAToLetterGrade(teamAssessment.positions.gpa)),
                          }}
                        >
                          {convertGPAToLetterGrade(teamAssessment.positions.gpa)}
                        </span>
                      </div>
                    </div>

                    {teamAssessment.positions.strongestPositions?.[0] && (
                      <div
                        style={{
                          padding: '.75rem',
                          background: '#f8fafc',
                          borderRadius: 6,
                          borderLeft: '4px solid #059669',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '.85rem', fontWeight: 500, color: '#374151' }}>💪 Top Strength</span>
                          <span style={{ fontSize: '.9rem', fontWeight: 'bold', color: '#059669' }}>
                            {teamAssessment.positions.strongestPositions[0]}
                          </span>
                        </div>
                      </div>
                    )}

                    {teamAssessment.positions.weakestPositions?.[0] && (
                      <div
                        style={{
                          padding: '.75rem',
                          background: '#f8fafc',
                          borderRadius: 6,
                          borderLeft: '4px solid #dc2626',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '.85rem', fontWeight: 500, color: '#374151' }}>⚠️ Needs Work</span>
                          <span style={{ fontSize: '.9rem', fontWeight: 'bold', color: '#dc2626' }}>
                            {teamAssessment.positions.weakestPositions[0]}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Coaching Overview */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4
                    style={{
                      margin: '0 0 .75rem 0',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: 'var(--sbe-primary)',
                    }}
                  >
                    👨‍🏫 Overall Coaching Staff Analysis
                  </h4>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '.75rem',
                    }}
                  >
                    <div
                      style={{
                        padding: '.75rem',
                        background: '#f8fafc',
                        borderRadius: 6,
                        borderLeft: `4px solid ${getGradeColor(
                          convertGPAToLetterGrade(teamAssessment.coaches.gpa)
                        )}`,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '.85rem', fontWeight: 500, color: '#374151' }}>
                          📊 Coaching Grade
                        </span>
                        <span
                          style={{
                            fontSize: '.9rem',
                            fontWeight: 'bold',
                            color: getGradeColor(convertGPAToLetterGrade(teamAssessment.coaches.gpa)),
                          }}
                        >
                          {convertGPAToLetterGrade(teamAssessment.coaches.gpa)}
                        </span>
                      </div>
                    </div>

                    {teamAssessment.coaches.strongestAreas?.[0] && (
                      <div
                        style={{
                          padding: '.75rem',
                          background: '#f8fafc',
                          borderRadius: 6,
                          borderLeft: '4px solid #059669',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '.85rem', fontWeight: 500, color: '#374151' }}>💪 Top Strength</span>
                          <span style={{ fontSize: '.9rem', fontWeight: 'bold', color: '#059669' }}>
                            {teamAssessment.coaches.strongestAreas[0]}
                          </span>
                        </div>
                      </div>
                    )}

                    {teamAssessment.coaches.weakestAreas?.[0] && (
                      <div
                        style={{
                          padding: '.75rem',
                          background: '#f8fafc',
                          borderRadius: 6,
                          borderLeft: '4px solid #dc2626',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '.85rem', fontWeight: 500, color: '#374151' }}>⚠️ Needs Work</span>
                          <span style={{ fontSize: '.9rem', fontWeight: 'bold', color: '#dc2626' }}>
                            {teamAssessment.coaches.weakestAreas[0]}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Key Insight */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4
                    style={{
                      margin: '0 0 .75rem 0',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: 'var(--sbe-primary)',
                    }}
                  >
                    💡 Key Insights
                  </h4>
                  <div
                    style={{
                      padding: '1rem',
                      background: '#f8fafc',
                      borderRadius: 6,
                      borderLeft: '4px solid var(--sbe-primary)',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '.9rem',
                        color: '#374151',
                        lineHeight: 1.5,
                        fontStyle: 'italic',
                      }}
                    >
                      {teamAssessment.summary.keyInsight}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Your Complete Analysis */}
        <div
          style={{
            background: 'white',
            borderRadius: 12,
            border: `2px solid ${isAnalysisLocked ? '#10b981' : '#e5e7eb'}`,
            overflow: 'hidden',
            marginBottom: 24,
          }}
        >
          <button
            onClick={() => toggleSection('completeResults')}
            style={{
              width: '100%',
              background: expandedSection === 'completeResults' ? 'var(--sbe-primary)' : '#f8fafc',
              color: expandedSection === 'completeResults' ? 'white' : '#374151',
              border: 'none',
              padding: '1.5rem',
              fontSize: '1.2rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'all 0.2s ease',
              scrollMarginTop: '120px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
              <span>📋</span>
              <div>
                <div style={{ fontSize: '1.2rem', fontWeight: 600, margin: 0 }}>Your Complete Analysis</div>
                <div style={{ fontSize: '.9rem', opacity: 0.8, marginTop: '.25rem' }}>
                  View your detailed team breakdown, coach evaluations, and predictions
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
              {isAnalysisLocked && <span style={{ fontSize: '.8rem' }}>🔒</span>}
              <span
                style={{
                  transform: expandedSection === 'completeResults' ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              >
                ▼
              </span>
            </div>
          </button>

          {expandedSection === 'completeResults' && (
            <div style={{ padding: '2rem', borderTop: '1px solid #e5e7eb' }}>
              {/* Season Summary */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h4
                  style={{
                    margin: '0 0 .75rem 0',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: 'var(--sbe-primary)',
                  }}
                >
                  🏆 Season Summary
                </h4>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '.75rem',
                  }}
                >
                  <div
                    style={{
                      padding: '.75rem',
                      background: '#f8fafc',
                      borderRadius: 6,
                      borderLeft: '4px solid #10b981',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '.85rem', fontWeight: 500, color: '#374151' }}>
                        📊 Predicted Record
                      </span>
                      <span style={{ fontSize: '.9rem', fontWeight: 'bold', color: '#10b981' }}>
                        {calculateGameByGameRecord()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Position Grades */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h4
                  style={{
                    margin: '0 0 .75rem 0',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: 'var(--sbe-primary)',
                  }}
                >
                  🏈 Position Group Grades
                </h4>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    gap: '.75rem',
                  }}
                >
                  {completion.requiredPositions.map((position) => {
                    const grade = getPositionGrade(position);
                    return (
                      <div
                        key={position}
                        style={{
                          padding: '.75rem',
                          background: '#f8fafc',
                          borderRadius: 6,
                          borderLeft: `4px solid ${getGradeColor(grade)}`,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '.85rem', fontWeight: 500, color: '#374151' }}>
                            {position}
                          </span>
                          <span style={{ fontSize: '.9rem', fontWeight: 'bold', color: getGradeColor(grade) }}>
                            {grade}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Coach Evaluations */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h4
                  style={{
                    margin: '0 0 .75rem 0',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: 'var(--sbe-primary)',
                  }}
                >
                  👨‍🏫 Coach Evaluations
                </h4>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '.75rem',
                  }}
                >
                  {completion.requiredCoachAreas.map((area) => {
                    const grade = getCoachGrade(area);
                    const displayName = area.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
                    return (
                      <div
                        key={area}
                        style={{
                          padding: '.75rem',
                          background: '#f8fafc',
                          borderRadius: 6,
                          borderLeft: `4px solid ${getGradeColor(grade)}`,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '.85rem', fontWeight: 500, color: '#374151' }}>
                            {displayName}
                          </span>
                          <span style={{ fontSize: '.9rem', fontWeight: 'bold', color: getGradeColor(grade) }}>
                            {grade}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Player Predictions */}
              {(currentPlayerPredictions?.offensive ||
                currentPlayerPredictions?.defensive ||
                currentPlayerPredictions?.specialTeams) && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4
                    style={{
                      margin: '0 0 .75rem 0',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: 'var(--sbe-primary)',
                    }}
                  >
                    ⭐ Player Award Predictions
                  </h4>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '.75rem',
                    }}
                  >
                    {currentPlayerPredictions.offensive && (
                      <div
                        style={{
                          padding: '.75rem',
                          background: '#f8fafc',
                          borderRadius: 6,
                          borderLeft: '4px solid #7c3aed',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '.85rem', fontWeight: 500, color: '#374151' }}>🏃 Offensive</span>
                          <span style={{ fontSize: '.9rem', fontWeight: 'bold', color: '#7c3aed' }}>
                            {currentPlayerPredictions.offensive}
                          </span>
                        </div>
                      </div>
                    )}
                    {currentPlayerPredictions.defensive && (
                      <div
                        style={{
                          padding: '.75rem',
                          background: '#f8fafc',
                          borderRadius: 6,
                          borderLeft: '4px solid #dc2626',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '.85rem', fontWeight: 500, color: '#374151' }}>🛡️ Defensive</span>
                          <span style={{ fontSize: '.9rem', fontWeight: 'bold', color: '#dc2626' }}>
                            {currentPlayerPredictions.defensive}
                          </span>
                        </div>
                      </div>
                    )}
                    {currentPlayerPredictions.specialTeams && (
                      <div
                        style={{
                          padding: '.75rem',
                          background: '#f8fafc',
                          borderRadius: 6,
                          borderLeft: '4px solid #059669',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '.85rem', fontWeight: 500, color: '#374151' }}>🦵 Special Teams</span>
                          <span style={{ fontSize: '.9rem', fontWeight: 'bold', color: '#059669' }}>
                            {currentPlayerPredictions.specialTeams}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Season Outlook */}
              {gamePredictions?.seasonOutlook && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4
                    style={{
                      margin: '0 0 .75rem 0',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: 'var(--sbe-primary)',
                    }}
                  >
                    📝 Season Outlook
                  </h4>
                  <div
                    style={{
                      padding: '1rem',
                      background: '#f8fafc',
                      borderRadius: 6,
                      borderLeft: '4px solid #059669',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '.9rem',
                        color: '#374151',
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {gamePredictions.seasonOutlook}
                    </div>
                  </div>
                </div>
              )}

              {/* Coach Rankings */}
              {getSortedCoachRankings().length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4
                    style={{
                      margin: '0 0 .75rem 0',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: 'var(--sbe-primary)',
                    }}
                  >
                    🏆 Coach Rankings
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
                    {getSortedCoachRankings()
                      .slice(0, 5)
                      .map(({ coach, rank }, index) => (
                        <div
                          key={coach}
                          style={{
                            background: '#f8fafc',
                            padding: '.75rem',
                            borderRadius: 6,
                            borderLeft: `4px solid ${index === 0 ? '#f59e0b' : '#6b7280'}`,
                            position: 'relative',
                          }}
                        >
                          {isAnalysisLocked && (
                            <div style={{ position: 'absolute', top: 8, right: 8, fontSize: 10, color: '#10b981' }}>🔒</div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                              <span style={{ fontSize: '.9rem', fontWeight: 600, color: '#374151' }}>#{rank}</span>
                              <span>{index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '🏅'}</span>
                              <span style={{ fontWeight: 500, color: '#374151' }}>{coach}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div
                style={{
                  marginTop: '1.5rem',
                  padding: '1rem',
                  background: isAnalysisLocked ? '#dcfce7' : '#f0f9ff',
                  borderRadius: 8,
                  border: `1px solid ${isAnalysisLocked ? '#10b981' : '#0ea5e9'}`,
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: '.9rem',
                    color: isAnalysisLocked ? 'var(--sbe-primary-dark)' : '#0369a1',
                  }}
                >
                  <strong>Analysis Status:</strong> {completion.completionPercentage}% complete
                  {isAnalysisLocked && <span> • Submitted and locked</span>}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Group Results */}
        {gameData?.groupId && (
          <div
            data-section-id="groupResults"
            style={{
              background: 'white',
              borderRadius: 12,
              border: `2px solid ${isAnalysisLocked ? '#10b981' : '#e5e7eb'}`,
              overflow: 'hidden',
              marginBottom: 24,
            }}
          >
            <button
              onClick={() => toggleSection('groupResults')}
              style={{
                width: '100%',
                background: expandedSection === 'groupResults' ? 'var(--sbe-primary)' : '#f8fafc',
                color: expandedSection === 'groupResults' ? 'white' : '#374151',
                border: 'none',
                padding: '1.5rem',
                fontSize: '1.2rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.2s ease',
                scrollMarginTop: '120px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
                <span>👥</span>
                <div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 600, margin: 0 }}>Group Results</div>
                  <div style={{ fontSize: '.9rem', opacity: 0.8, marginTop: '.25rem' }}>
                    Compare your analysis with group members
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                <span
                  style={{
                    fontSize: 12,
                    backgroundColor: expandedSection === 'groupResults' ? 'rgba(255,255,255,0.2)' : '#10b981',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: 4,
                    fontWeight: 500,
                  }}
                >
                  Active
                </span>
                {isAnalysisLocked && <span style={{ fontSize: '.8rem' }}>🔒</span>}
                <span
                  style={{
                    transform: expandedSection === 'groupResults' ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                >
                  ▼
                </span>
              </div>
            </button>

            {expandedSection === 'groupResults' && (
              <div style={{ padding: 0, borderTop: '1px solid #e5e7eb' }}>
                <GroupResultsComponent gameData={gameData} />
              </div>
            )}
          </div>
        )}

        {/* Social Sharing (after submission) */}
        {completion.isSubmitted && (
          <SocialSharingComponent
            gameData={gameData}
            teamAssessment={teamAssessment}
            predictions={gamePredictions}
            playerPredictions={currentPlayerPredictions}
          />
        )}
      </div>
    </div>
  );
};

// GPA → letter
function convertGPAToLetterGrade(gpa) {
  if (gpa >= 3.85) return 'A+';
  if (gpa >= 3.7) return 'A';
  if (gpa >= 3.5) return 'A-';
  if (gpa >= 3.3) return 'B+';
  if (gpa >= 3.0) return 'B';
  if (gpa >= 2.7) return 'B-';
  if (gpa >= 2.3) return 'C+';
  if (gpa >= 2.0) return 'C';
  if (gpa >= 1.7) return 'C-';
  if (gpa >= 1.3) return 'D+';
  if (gpa >= 1.0) return 'D';
  if (gpa >= 0.7) return 'D-';
  return 'F';
}

// (kept for potential future mapping needs)
function formatPositionName(position) {
  const positionNames = {
    QB: 'Quarterback',
    RB: 'Running Back',
    WR: 'Wide Receiver',
    TE: 'Tight End',
    OL: 'Offensive Line',
    DL: 'Defensive Line',
    LB: 'Linebacker',
    DB: 'Defensive Back',
    K: 'Kicker',
    ST: 'Special Teams',
  };
  return positionNames[position] || position.replace(/_/g, ' ');
}

export default ResultsTab;
